import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { PaymentService } from '@/services/payment';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';
import { useNotify } from '@/hooks/useNotify';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface OneTimePaymentButtonProps {
  amount: number;
  currency?: string;
  name: string;
  description?: string;
  children: React.ReactNode;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  metadata?: Record<string, string>;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  organizationId?: string; // Added for multi-tenant support
  isPlatformPayment?: boolean; // Flag to indicate if this payment goes to the platform
}

const OneTimePaymentButton: React.FC<OneTimePaymentButtonProps> = ({
  amount,
  currency = 'usd',
  name,
  description,
  children,
  variant = 'default',
  size = 'default',
  className,
  metadata,
  onSuccess,
  onError,
  organizationId,
  isPlatformPayment = false,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const { success, error: notifyError } = useNotify();
  
  // Track retries to avoid flooding the API
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;

  const handlePayment = async () => {
    try {
      // Reset retry count on new payment attempt
      setRetryCount(0);
      setLoading(true);
      
      if (!user?.email) {
        const error = new Error('User email is required for payment processing');
        toast.error('Authentication required', {
          description: 'Please sign in to make a payment'
        });
        onError?.(error);
        return;
      }

      // Validate amount before proceeding
      if (!amount || amount <= 0) {
        const error = new Error('Invalid payment amount');
        toast.error('Invalid amount', {
          description: 'Please enter a valid payment amount'
        });
        onError?.(error);
        return;
      }

      const orgId = organizationId || user?.user_metadata?.organization_id;
      // Check if Stripe is connected for this org (unless platform payment)
      if (!isPlatformPayment && orgId) {
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('billing_settings')
          .eq('id', orgId)
          .single();
        let stripeConnected = false;
        if (!orgError && orgData?.billing_settings) {
          const billing = typeof orgData.billing_settings === 'string' ? JSON.parse(orgData.billing_settings) : orgData.billing_settings;
          stripeConnected = !!billing?.stripe_account_id;
        }
        if (!stripeConnected) {
          toast.error('Online payment is not available for this organization. Please contact the activity center for payment options.');
          setLoading(false);
          return;
        }
      }

      console.log('Creating one-time payment', {
        amount,
        currency,
        name,
        description,
        customer_email: user?.email,
        organization_id: orgId,
        isPlatformPayment
      });

      // Display processing toast that we can update later
      const toastId = toast.loading('Processing payment...', {
        duration: 10000 // 10 seconds should be enough for most payment processing
      });

      const result = await PaymentService.createOneTimePayment({
        amount,
        currency,
        name,
        description,
        customer_email: user?.email,
        success_url: `${window.location.origin}/payment-success`,
        cancel_url: `${window.location.origin}/payment-canceled`,
        organization_id: orgId,
        isPlatformPayment,
        applicationFeePercent: 5, // Default to 5%
        metadata: {
          user_id: user?.id,
          organization_id: orgId || '',
          is_platform_payment: isPlatformPayment ? 'true' : 'false',
          ...metadata,
        },
      });

      // Dismiss the loading toast
      toast.dismiss(toastId);

      if (result?.url) {
        // Call optional success callback
        onSuccess?.();
        
        // Show success message before redirect
        success('Payment initiated successfully', {
          description: 'Redirecting to secure payment page...'
        });
        
        // Short delay to show the success message before redirect
        setTimeout(() => {
          // Redirect to Stripe checkout
          window.location.href = result.url;
        }, 1000);
      } else if (result?.error) {
        // Handle specific error types from the edge function response
        if (typeof result.error === 'string' && result.error.includes('platform profile')) {
          // Special case for incomplete Stripe Connect setup
          toast.error('Stripe platform setup required', {
            description: 'Your Stripe platform account requires additional setup to process payments',
            action: {
              label: 'Setup Stripe',
              onClick: () => window.open("https://dashboard.stripe.com/connect/accounts/overview", "_blank")
            }
          });
          
          const setupError = new Error('Stripe platform setup required');
          onError?.(setupError);
          return;
        }
        
        throw new Error(`Payment service error: ${result.error}`);
      } else {
        throw new Error('Failed to create payment session - no URL returned');
      }
    } catch (error: any) {
      const context: ErrorContext = {
        operation: 'createOneTimePayment',
        additionalInfo: { amount, currency, name, retryCount }
      };
      
      handleError(
        error,
        "Payment processing failed",
        ErrorSeverity.ERROR,
        context,
        true
      );
      
      // More descriptive error handling based on error type
      let errorDescription = 'Please try again later';
      let errorTitle = 'Payment failed';
      
      if (error.message.includes('network') || error.message.includes('connection')) {
        errorDescription = 'Please check your internet connection and try again';
        errorTitle = 'Connection issue';
      } else if (error.message.includes('declined') || error.message.includes('card')) {
        errorDescription = 'Your payment method was declined. Please try a different payment method';
        errorTitle = 'Payment declined';
      } else if (error.message.includes('Stripe') && error.message.includes('setup')) {
        errorDescription = 'The payment system requires additional setup';
        errorTitle = 'Configuration required';
      } else if (retryCount < MAX_RETRIES) {
        // Only offer retry for certain errors and if within retry limit
        errorDescription = 'Tap to retry payment';
        toast.error(errorTitle, {
          description: errorDescription,
          action: {
            label: 'Retry',
            onClick: () => {
              setRetryCount(prev => prev + 1);
              handlePayment();
            }
          }
        });
        
        // Call optional error callback
        onError?.(error);
        setLoading(false);
        return;
      }
      
      notifyError(errorTitle, {
        description: error.message || errorDescription
      });
      
      // Call optional error callback
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={handlePayment}
      disabled={loading}
    >
      {loading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Processing...
        </>
      ) : children}
    </Button>
  );
};

export default OneTimePaymentButton;
